import { createContext, useContext, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';

type CandidateSessionMode = 'anonymous' | 'authenticated';

interface CandidateSession {
  mode: CandidateSessionMode;
  shareId: string | null;
  isAnonymous: boolean;
  isAuthenticated: boolean;
}

const CandidateSessionContext = createContext<CandidateSession | null>(null);

interface CandidateSessionProviderProps {
  mode: CandidateSessionMode;
  shareId?: string;
  children: ReactNode;
}

export function CandidateSessionProvider({
  mode,
  shareId,
  children,
}: CandidateSessionProviderProps): JSX.Element {
  const value = useMemo<CandidateSession>(
    () => ({
      mode,
      shareId: shareId ?? null,
      isAnonymous: mode === 'anonymous',
      isAuthenticated: mode === 'authenticated',
    }),
    [mode, shareId]
  );

  return (
    <CandidateSessionContext.Provider value={value}>{children}</CandidateSessionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCandidateSession(): CandidateSession {
  const context = useContext(CandidateSessionContext);
  if (!context) {
    throw new Error('useCandidateSession must be used within CandidateSessionProvider');
  }
  return context;
}
